import React, { useEffect } from 'react';
import { useLocation } from 'react-router';
import { Route, Switch } from 'react-router-dom';

import { myAccount } from '@portal/config/app.json';
import { getRouteStackPath } from '@portal/config/routes';
import { PageClass } from '@portal/enum/analytics';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { sendEvent } from '@portal/services/firebase';
import { translate } from '@portal/services/i18n';

import AccountMultiAccess from '@portal/pages/MyAccount/MyAccountPage/MultiAccess/AccountMultiAccess/AccountMultiAccess';
import CredentialMultiAccess from '@portal/pages/MyAccount/MyAccountPage/MultiAccess/CredentialMultiAccess/CredentialMultiAccess';
import EditExternalUser from '@portal/pages/MyAccount/MyAccountPage/MultiAccess/EditExternalUser/EditExternalUser';
import EditMultiAccessUser from '@portal/pages/MyAccount/MyAccountPage/MultiAccess/EditMultiAccessUser/EditMultiAccessUser';
import InviteUser from '@portal/pages/MyAccount/MyAccountPage/MultiAccess/InviteUser/InviteUser';
import MyPermissionsMultiAccess from '@portal/pages/MyAccount/MyAccountPage/MultiAccess/MyPermissionsMultiAccess/MyPermissionsMultiAccess';
import PermissionsMultiAccess from '@portal/pages/MyAccount/MyAccountPage/MultiAccess/PermissionsMultiAccess/PermissionsMultiAccess';

import PanelContent from '@portal/components/PanelContent/PanelContent';
import useReduxState from '@portal/hooks/useReduxState';
import AlterPassword from '@portal/pages/MyAccount/MyAccountPage/AlterPassword/AlterPassword';
import Configuration from '@portal/pages/MyAccount/MyAccountPage/Configuration/Configuration';
import Faq from '@portal/pages/MyAccount/MyAccountPage/Faq/Faq';
import IncomeTax from '@portal/pages/MyAccount/MyAccountPage/IncomeTax/IncomeTax';
import MultiAccessScreen from '@portal/pages/MyAccount/MyAccountPage/MultiAccess/MultiAccess';
import MyAccountPage from '@portal/pages/MyAccount/MyAccountPage/MyAccountPage';
import PayStub from '@portal/pages/MyAccount/MyAccountPage/PayStub/PayStub';
import PersonalInformation from '@portal/pages/MyAccount/MyAccountPage/PersonalInformation/PersonalInformation';
import SalaryPortability from '@portal/pages/MyAccount/MyAccountPage/SalaryPortability/SalaryPortability';
import MyDevices from './MyAccountPage/MyDevices/MyDevices';

const MyAccountNavigationStack = () => {
  const { type } = useReduxState().user.me.account;
  const location = useLocation();

  useEffect(() => {
    sendEvent('page_view', {
      firebase_page: location.pathname,
      firebase_page_class: PageClass.MY_ACCOUNT,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <Switch>
      <Route exact path={getRouteStackPath('PROFILE', 'PROFILE_PAGE')}>
        <PanelContent pageTitle={translate('PAGES.MY_ACCOUNT.TABS.MY_ACCOUNT')}>
          <MyAccountPage />
        </PanelContent>
      </Route>
      <Route exact path={getRouteStackPath('PROFILE', 'PROFILE_INFORMATION')}>
        <PanelContent
          pageTitle={translate('PAGES.MY_ACCOUNT.TABS.PERSONAL_INFORMATION')}
        >
          <PersonalInformation />
        </PanelContent>
      </Route>
      <Route exact path={getRouteStackPath('PROFILE', 'MY_DEVICES')}>
        <PanelContent
          pageTitle={translate('PAGES.MY_ACCOUNT.TABS.PERSONAL_INFORMATION')}
        >
          <MyDevices />
        </PanelContent>
      </Route>
      <Route exact path={getRouteStackPath('PROFILE', 'ALTER_PASSWORD')}>
        <PanelContent
          pageTitle={translate('PAGES.MY_ACCOUNT.TABS.CHANGE_PASSWORD_STACK')}
        >
          <AlterPassword />
        </PanelContent>
      </Route>
      <Route exact path={getRouteStackPath('PROFILE', 'INCOME_TAX')}>
        <PanelContent pageTitle={translate('PAGES.MY_ACCOUNT.TABS.INCOME_TAX')}>
          <IncomeTax />
        </PanelContent>
      </Route>
      {(myAccount.multiAccessPf && type === AccountTaxIdentifierType.PERSON) ||
      (myAccount.multiAccessPj && type === AccountTaxIdentifierType.CORPORATE)
        ? [
            <Route
              key="MULTI_ACCESS"
              exact
              path={getRouteStackPath('PROFILE', 'MULTI_ACCESS')}
            >
              <PanelContent
                pageTitle={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.ACCOUNT')}
              >
                <MultiAccessScreen />
              </PanelContent>
            </Route>,
            <Route
              key="ACCOUNT_MULTI_ACCESS"
              exact
              path={getRouteStackPath('PROFILE', 'ACCOUNT_MULTI_ACCESS')}
            >
              <PanelContent
                pageTitle={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.CREATE_NEW_USER'
                )}
              >
                <AccountMultiAccess />
              </PanelContent>
            </Route>,
            <Route
              key="CREDENTIAL_MULTI_ACCESS"
              exact
              path={getRouteStackPath('PROFILE', 'CREDENTIAL_MULTI_ACCESS')}
            >
              <PanelContent
                pageTitle={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.CREDENTIAL'
                )}
              >
                <CredentialMultiAccess />
              </PanelContent>
            </Route>,
            <Route
              key="PERMISSIONS_MULTI_ACCESS"
              exact
              path={getRouteStackPath('PROFILE', 'PERMISSIONS_MULTI_ACCESS')}
            >
              <PanelContent
                pageTitle={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.PERMISSIONS'
                )}
              >
                <PermissionsMultiAccess />
              </PanelContent>
            </Route>,
            <Route
              key="MY_PERMISSIONS_MULTI_ACCESS"
              exact
              path={getRouteStackPath('PROFILE', 'MY_PERMISSIONS_MULTI_ACCESS')}
            >
              <PanelContent
                pageTitle={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.MY_PERMISSIONS'
                )}
              >
                <MyPermissionsMultiAccess />
              </PanelContent>
            </Route>,
            <Route
              key="EDIT_MULTI_ACCESS_USER"
              exact
              path={getRouteStackPath('PROFILE', 'EDIT_MULTI_ACCESS_USER')}
            >
              <PanelContent
                pageTitle={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_USER_INFO'
                )}
              >
                <EditMultiAccessUser />
              </PanelContent>
            </Route>,
            <Route
              key="INVITE_USER"
              exact
              path={getRouteStackPath('PROFILE', 'INVITE_USER')}
            >
              <PanelContent
                pageTitle={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.INVITE_USER'
                )}
              >
                <InviteUser />
              </PanelContent>
            </Route>,
            <Route
              key="EDIT_EXTERNAL_USER"
              exact
              path={getRouteStackPath('PROFILE', 'EDIT_EXTERNAL_USER')}
            >
              <PanelContent
                pageTitle={translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.TITLE'
                )}
              >
                <EditExternalUser />
              </PanelContent>
            </Route>,
          ]
        : null}
      {myAccount.payStub ? (
        <Route exact path={getRouteStackPath('PROFILE', 'PAY_STUB')}>
          <PanelContent
            pageTitle={translate('PAGES.MY_ACCOUNT.PAY_STUB.PAGE_TITLE')}
            className="my-account-navigation--unpadded"
          >
            <PayStub />
          </PanelContent>
        </Route>
      ) : null}

      {(myAccount.salaryPortabilityPF &&
        type === AccountTaxIdentifierType.PERSON) ||
      (myAccount.salaryPortabilityPJ &&
        type === AccountTaxIdentifierType.CORPORATE) ? (
        <Route exact path={getRouteStackPath('PROFILE', 'SALARY_PORTABILITY')}>
          <PanelContent
            pageTitle={translate('PAGES.MY_ACCOUNT.TABS.SALARY_PORTABILITY')}
          >
            <SalaryPortability />
          </PanelContent>
        </Route>
      ) : null}

      <Route exact path={getRouteStackPath('PROFILE', 'CONFIGURATION')}>
        <PanelContent
          pageTitle={translate('PAGES.MY_ACCOUNT.TABS.CONFIGURATION')}
        >
          <Configuration />
        </PanelContent>
      </Route>
      <Route exact path={getRouteStackPath('PROFILE', 'FAQ')}>
        <PanelContent pageTitle={translate('PAGES.MY_ACCOUNT.TABS.FAQ')}>
          <Faq />
        </PanelContent>
      </Route>
    </Switch>
  );
};

export default MyAccountNavigationStack;
